// The branded chat page's colour engine.
//
// ⚠️ This file exists twice, byte for byte: `frontend-landing/src/lib/` (the
// public page at jawabai.cloud/c/<name>) and `frontend-app/src/lib/` (the
// dashboard preview). The two apps are separate builds, so they cannot share a
// module. `brandPageTheme.test.js` fails if the copies drift — change one,
// copy it over the other.
//
// The rule it implements: the business picks a main colour, an optional
// accent, a background style and light/dark. It never picks a text colour.
// Every colour on the page is derived from those choices, and every text
// colour is measured (WCAG relative luminance) to clear 4.5:1 against what it
// sits on. When the business's own colour cannot carry readable text, the
// colour is nudged darker or lighter in small steps until it can, and
// `adjusted` says so, so the dashboard can tell the owner rather than quietly
// showing a different colour. See docs/branded-page.md §Colour engine.

export const DEFAULT_BRAND = '#9d59f8'; // jawabai purple, from theme.css

const AA = 4.5;
const WHITE = '#ffffff';
const BLACK = '#000000';
const INK = '#0b0f14'; // the dark text colour; softer than pure black
const LIGHT_TEXT = '#f3f4f6';

// '#rrggbb', 'rrggbb' or '#rgb' → '#rrggbb' (lower case); anything else → null.
export function normalizeHex(value) {
  let hex = String(value || '').trim().replace(/^#/, '');
  if (/^[0-9a-fA-F]{3}$/.test(hex)) hex = hex.split('').map((c) => c + c).join('');
  if (!/^[0-9a-fA-F]{6}$/.test(hex)) return null;
  return `#${hex.toLowerCase()}`;
}

function channels(hex) {
  return [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
}

function toHex(rgb) {
  return `#${rgb
    .map((v) => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, '0'))
    .join('')}`;
}

// `amount` of the way from a to b (0 = a, 1 = b).
function mix(a, b, amount) {
  const A = channels(a);
  const B = channels(b);
  return toHex(A.map((v, i) => v + (B[i] - v) * amount));
}

function rgba(hex, alpha) {
  const [r, g, b] = channels(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function luminance(hex) {
  const [r, g, b] = channels(hex).map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrastRatio(a, b) {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

function worstContrast(color, backgrounds) {
  return Math.min(...backgrounds.map((bg) => contrastRatio(color, bg)));
}

// Walk `color` toward `toward` (pure black or white) until it reaches `target`
// against every background. Bounded: 40 steps of 8% leave under 4% of the
// original, which is effectively `toward` itself.
function reach(color, backgrounds, target, toward) {
  let out = color;
  for (let i = 0; i < 40 && worstContrast(out, backgrounds) < target; i++) {
    out = mix(out, toward, 0.08);
  }
  return out;
}

// A filled colour (button, visitor bubble, logo circle) and the text on it.
// Text is white or near-black, whichever reads better; if neither reaches
// 4.5:1 the fill moves away from the text colour until one does.
function filled(color) {
  const fg = contrastRatio(color, WHITE) >= contrastRatio(color, INK) ? WHITE : INK;
  const fill = reach(color, [fg], AA, fg === WHITE ? BLACK : WHITE);
  return { fill, fg };
}

/**
 * @param {object} input
 * @param {string} input.brand       main colour, any hex; bad/empty → jawabai purple
 * @param {string} [input.accent]    second colour; bad/empty → follows the brand
 * @param {'light'|'dark'} [input.mode]  anything but 'dark' is light; the page
 *                                   resolves 'auto' itself and asks for both
 * @param {'plain'|'gradient'|'cover'} [input.background]
 * @param {boolean} [input.hasCover] 'cover' without a photo falls back to 'gradient'
 */
export function brandPageTheme({ brand, accent, mode, background, hasCover } = {}) {
  const dark = mode === 'dark';
  const chosenBrand = normalizeHex(brand) || DEFAULT_BRAND;
  const chosenAccent = normalizeHex(accent) || chosenBrand;
  const far = dark ? WHITE : BLACK;

  // Page surfaces, lightly tinted with the brand so the page feels like theirs.
  const bg = dark ? mix('#090b0e', chosenBrand, 0.1) : mix(WHITE, chosenBrand, 0.05);
  const surface = dark ? mix('#13161a', chosenBrand, 0.09) : WHITE;
  const surface2 = dark ? mix(surface, WHITE, 0.05) : mix(WHITE, chosenBrand, 0.045);
  const surfaces = [bg, surface, surface2];

  const text = reach(
    dark ? mix(LIGHT_TEXT, chosenBrand, 0.04) : mix(INK, chosenBrand, 0.1),
    surfaces,
    AA,
    far,
  );
  const muted = reach(mix(text, surface, 0.42), surfaces, AA, far);
  const border = mix(surface, dark ? WHITE : text, 0.1);

  // Make each colour stand out from the page first (a near-black brand on a
  // dark page would vanish), then make sure text on it is readable.
  const visible = dark ? 2.4 : 1.3;
  const brandFill = filled(reach(chosenBrand, [bg], visible, far));
  const accentFill = filled(reach(chosenAccent, [bg], visible, far));

  // The same colours used as text or icons on the page (links, handles, icons).
  const brandInk = reach(brandFill.fill, surfaces, AA, far);
  const accentInk = reach(accentFill.fill, surfaces, AA, far);

  const style = background === 'cover' && !hasCover ? 'gradient' : background || 'plain';
  const b = brandFill.fill;
  const a = accentFill.fill;

  const vars = {
    '--brand': b,
    '--brand-fg': brandFill.fg,
    '--brand-ink': brandInk,
    '--brand-tint': rgba(brandInk, dark ? 0.16 : 0.09),
    '--brand-line': rgba(brandInk, 0.32),
    '--accent': a,
    '--accent-fg': accentFill.fg,
    '--accent-ink': accentInk,
    '--accent-tint': rgba(accentInk, dark ? 0.18 : 0.12),
    '--bg': bg,
    '--surface': surface,
    '--surface-2': surface2,
    '--text': text,
    '--text-muted': muted,
    '--border': border,
    // A soft wash over the page background — 'gradient' style only.
    '--bg-image':
      style === 'gradient'
        ? `radial-gradient(120% 55% at 100% 0%, ${rgba(b, dark ? 0.22 : 0.16)} 0, transparent 62%), ` +
          `radial-gradient(110% 50% at 0% 100%, ${rgba(a, dark ? 0.16 : 0.13)} 0, transparent 62%)`
        : 'none',
    // Stands in for the cover photo when there is none.
    '--hero-grad':
      `radial-gradient(90% 120% at 85% 0%, ${rgba(mix(WHITE, a, 0.6), 0.55)} 0, transparent 60%), ` +
      `linear-gradient(125deg, ${mix(bg, b, 0.62)} 0%, ${mix(bg, a, 0.5)} 100%)`,
    // Laid over the blurred cover photo — 'cover' style only — so text on the
    // page is read against the page colour, not the photo.
    '--cover-veil': `linear-gradient(to bottom, ${rgba(bg, 0.45)}, ${rgba(bg, 0.82)})`,
    '--chat-bg': style === 'plain' ? bg : rgba(bg, 0.5),
  };

  return {
    vars,
    mode: dark ? 'dark' : 'light',
    background: style,
    // An accent the business did not pick is never reported as "adjusted".
    adjusted: { brand: b !== chosenBrand, accent: Boolean(normalizeHex(accent)) && a !== chosenAccent },
    ratios: {
      brandFg: contrastRatio(b, brandFill.fg),
      accentFg: contrastRatio(a, accentFill.fg),
    },
  };
}
